import { useState, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '@/lib/supabase'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export interface BioLink {
  id: string
  bio_page_id: string
  title: string
  url: string
  icon: string | null
  thumbnail_url: string | null
  order_index: number
  is_active: boolean
  type: string
  click_count: number
  scheduled_start_at: string | null
  scheduled_end_at: string | null
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_enabled: boolean
  masked_slug: string | null
}

interface Props {
  pageId: string
  links: BioLink[]
}

const SOCIAL_PLATFORMS: { domains: string[]; bg: string; iconColor: string; path: string }[] = [
  { domains: ['youtube.com', 'youtu.be'], bg: 'bg-red-50 dark:bg-red-900/20', iconColor: '#FF0000', path: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' },
  { domains: ['twitter.com', 'x.com'], bg: 'bg-gray-50 dark:bg-gray-900/40', iconColor: '#000000', path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
  { domains: ['instagram.com'], bg: 'bg-pink-50 dark:bg-pink-900/20', iconColor: '#E1306C', path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162S8.597 18.163 12 18.163s6.162-2.759 6.162-6.162S15.403 5.838 12 5.838zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' },
  { domains: ['linkedin.com'], bg: 'bg-blue-50 dark:bg-blue-900/20', iconColor: '#0A66C2', path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' },
  { domains: ['github.com'], bg: 'bg-gray-50 dark:bg-gray-900/40', iconColor: '#181717', path: 'M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12' },
  { domains: ['tiktok.com'], bg: 'bg-gray-50 dark:bg-gray-900/40', iconColor: '#000000', path: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z' },
  { domains: ['facebook.com', 'fb.com'], bg: 'bg-blue-50 dark:bg-blue-900/20', iconColor: '#1877F2', path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' },
  { domains: ['spotify.com'], bg: 'bg-green-50 dark:bg-green-900/20', iconColor: '#1DB954', path: 'M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z' },
  { domains: ['discord.com', 'discord.gg'], bg: 'bg-indigo-50 dark:bg-indigo-900/20', iconColor: '#5865F2', path: 'M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057.1 18.08.114 18.1.134 18.114a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z' },
]

function getSocialPlatform(url: string) {
  if (!url) return null
  try {
    const hostname = new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace('www.', '')
    return SOCIAL_PLATFORMS.find(p => p.domains.some(d => hostname === d || hostname.endsWith(`.${d}`))) ?? null
  } catch {
    return null
  }
}

function detectLinkType(url: string): string {
  if (!url) return 'link'
  if (url.startsWith('mailto:')) return 'email'
  if (url.startsWith('tel:')) return 'phone'
  return 'link'
}

function getLinkTypeConfig(type: string): { bg: string; color: string; icon: string } {
  switch (type) {
    case 'social': return { bg: 'bg-blue-50 dark:bg-blue-900/20', color: 'text-blue-500', icon: 'share' }
    case 'email': return { bg: 'bg-orange-50 dark:bg-orange-900/20', color: 'text-orange-500', icon: 'mail' }
    case 'phone': return { bg: 'bg-green-50 dark:bg-green-900/20', color: 'text-green-500', icon: 'call' }
    case 'embed': return { bg: 'bg-red-50 dark:bg-red-900/20', color: 'text-red-500', icon: 'smart_display' }
    default: return { bg: 'bg-violet-50 dark:bg-violet-900/20', color: 'text-violet-500', icon: 'link' }
  }
}

function SortableLinkItem({ link, editingId, setEditingId, updateMutation, deleteMutation }: {
  link: BioLink
  editingId: string | null
  setEditingId: (id: string | null) => void
  updateMutation: ReturnType<typeof useMutation<unknown, Error, { linkId: string; data: Partial<BioLink> }>>
  deleteMutation: ReturnType<typeof useMutation<unknown, Error, string>>
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: link.id })
  const isEditing = editingId === link.id
  const typeConfig = getLinkTypeConfig(link.type)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  }

  if (isEditing) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="bg-white dark:bg-[#1c2230] rounded-xl border border-[#2B6CEE]/30 ring-1 ring-[#2B6CEE]/20 shadow-lg overflow-hidden"
      >
        {/* Expanded header - compact info */}
        <div className="h-16 flex items-center gap-3 px-4">
          {/* Drag handle */}
          <button
            {...attributes}
            {...listeners}
            className="text-gray-300 hover:text-gray-400 cursor-grab active:cursor-grabbing touch-none"
          >
            <span className="material-symbols-outlined text-[18px]">drag_indicator</span>
          </button>

          {/* Icon badge */}
          {(() => {
            const social = getSocialPlatform(link.url)
            if (link.icon) {
              return (
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${typeConfig.bg}`}>
                  <span className="text-lg">{link.icon}</span>
                </div>
              )
            }
            if (social) {
              return (
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${social.bg}`}>
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill={social.iconColor}>
                    <path d={social.path} />
                  </svg>
                </div>
              )
            }
            return (
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${typeConfig.bg}`}>
                <span className={`material-symbols-outlined text-[18px] ${typeConfig.color}`}>{typeConfig.icon}</span>
              </div>
            )
          })()}

          {/* Title / URL */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#0d121b] dark:text-white truncate">{link.title}</p>
            <p className="text-xs text-gray-400 truncate">{link.url}</p>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-[#e7ebf3] dark:bg-white/10" />

        {/* Edit form */}
        <div className="p-4">
          <EditLinkForm
            link={link}
            onSave={(data) => updateMutation.mutate({ linkId: link.id, data })}
            onCancel={() => setEditingId(null)}
            saving={updateMutation.isPending}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white dark:bg-[#1c2230] rounded-xl border border-[#e7ebf3] dark:border-white/10 h-16 flex items-center gap-3 px-4 shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="text-gray-300 hover:text-gray-400 cursor-grab active:cursor-grabbing touch-none"
      >
        <span className="material-symbols-outlined text-[18px]">drag_indicator</span>
      </button>

      {/* Icon badge 40x40 */}
      {(() => {
        const social = getSocialPlatform(link.url)
        if (link.icon) {
          return (
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${typeConfig.bg}`}>
              <span className="text-lg">{link.icon}</span>
            </div>
          )
        }
        if (social) {
          return (
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${social.bg}`}>
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill={social.iconColor}>
                <path d={social.path} />
              </svg>
            </div>
          )
        }
        return (
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${typeConfig.bg}`}>
            <span className={`material-symbols-outlined text-[18px] ${typeConfig.color}`}>{typeConfig.icon}</span>
          </div>
        )
      })()}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#0d121b] dark:text-white truncate">{link.title}</p>
        <p className="text-xs text-gray-400 truncate">{link.url}</p>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs text-gray-400 hidden sm:block">{link.click_count} clicks</span>

        {/* Pill toggle */}
        <button
          onClick={() => updateMutation.mutate({ linkId: link.id, data: { is_active: !link.is_active } })}
          className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
            link.is_active ? 'bg-[#2B6CEE]' : 'bg-gray-200 dark:bg-gray-600'
          }`}
          title={link.is_active ? 'Active – click to deactivate' : 'Inactive – click to activate'}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              link.is_active ? 'translate-x-4' : 'translate-x-0'
            }`}
          />
        </button>

        {/* Edit button */}
        <button
          onClick={() => setEditingId(link.id)}
          className="p-1.5 text-gray-400 hover:text-[#2B6CEE] hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
          title="Edit"
        >
          <span className="material-symbols-outlined text-[16px]">edit</span>
        </button>

        {/* Delete button */}
        <button
          onClick={() => { if (confirm('Delete this link?')) deleteMutation.mutate(link.id) }}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          title="Delete"
        >
          <span className="material-symbols-outlined text-[16px]">delete</span>
        </button>
      </div>
    </div>
  )
}

export function BioLinkEditor({ pageId, links }: Props) {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [newLink, setNewLink] = useState({ title: '', url: '', type: 'link', icon: '' })
  const [showAdd, setShowAdd] = useState(false)
  const [fetchingOg, setFetchingOg] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleUrlPaste = useCallback(async (url: string) => {
    if (!url || !url.match(/^https?:\/\//)) return
    setFetchingOg(true)
    try {
      const res = await apiRequest('/api/bio-pages/og-fetch', 'POST', { url }) as { success: boolean; data: { title: string | null } }
      if (res.success && res.data?.title && !newLink.title) {
        setNewLink(p => ({ ...p, title: res.data.title! }))
      }
    } catch {
      // ignore OG fetch errors
    } finally {
      setFetchingOg(false)
    }
  }, [newLink.title])

  const addMutation = useMutation({
    mutationFn: (data: { title: string; url: string; type: string; icon?: string }) =>
      apiRequest(`/api/bio-pages/${pageId}/links`, 'POST', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bio-links', pageId] })
      setNewLink({ title: '', url: '', type: 'link', icon: '' })
      setShowAdd(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ linkId, data }: { linkId: string; data: Partial<BioLink> }) =>
      apiRequest(`/api/bio-pages/${pageId}/links/${linkId}`, 'PUT', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bio-links', pageId] })
      setEditingId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (linkId: string) =>
      apiRequest(`/api/bio-pages/${pageId}/links/${linkId}`, 'DELETE'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bio-links', pageId] })
    },
  })

  const reorderMutation = useMutation({
    mutationFn: (linkIds: string[]) =>
      apiRequest(`/api/bio-pages/${pageId}/links/reorder`, 'POST', { linkIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bio-links', pageId] })
    },
  })

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = links.findIndex(l => l.id === active.id)
    const newIndex = links.findIndex(l => l.id === over.id)
    const newOrder = arrayMove(links, oldIndex, newIndex)
    reorderMutation.mutate(newOrder.map(l => l.id))
  }

  return (
    <div className="space-y-4">
      {/* Header row: count + add button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {links.length} {links.length === 1 ? 'link' : 'links'}
        </p>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${
            showAdd
              ? 'bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300'
              : 'bg-[#2B6CEE] text-white hover:bg-[#2454c7] shadow-sm shadow-blue-500/20'
          }`}
        >
          <span className="material-symbols-outlined text-[18px]">{showAdd ? 'remove' : 'add'}</span>
          {showAdd ? 'Cancel' : 'Add Link'}
        </button>
      </div>

      {/* Add link form */}
      {showAdd && (
        <div className="bg-white dark:bg-[#1c2230] p-6 rounded-xl border border-[#e7ebf3] dark:border-white/10 shadow-lg space-y-4 animate-in fade-in slide-in-from-top-4 duration-200">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
              URL {fetchingOg && <span className="text-[#2B6CEE] ml-2 font-normal normal-case animate-pulse">Fetching title...</span>}
            </label>
            <input
              type="text"
              value={newLink.url}
              onChange={e => setNewLink(p => ({ ...p, url: e.target.value }))}
              onPaste={e => {
                const pasted = e.clipboardData.getData('text')
                if (pasted) setTimeout(() => handleUrlPaste(pasted), 100)
              }}
              placeholder="https://example.com"
              className="w-full px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] placeholder:text-gray-400"
              autoFocus
            />
            <p className="text-xs text-gray-400 mt-1">Paste any link — we'll detect the type automatically</p>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">Title</label>
            <input
              type="text"
              value={newLink.title}
              onChange={e => setNewLink(p => ({ ...p, title: e.target.value }))}
              placeholder="e.g. My Portfolio"
              className="w-full px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] placeholder:text-gray-400"
            />
          </div>

          {addMutation.isError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 text-sm rounded-lg border border-red-100 dark:border-red-900/20">
              {(addMutation.error as Error)?.message || 'Failed to add link'}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button
              onClick={() => setShowAdd(false)}
              className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => addMutation.mutate({ title: newLink.title, url: newLink.url, type: detectLinkType(newLink.url) })}
              disabled={!newLink.title || !newLink.url || addMutation.isPending}
              className="px-6 py-2 text-sm font-medium text-white bg-[#2B6CEE] rounded-lg hover:bg-[#2454c7] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-blue-500/20 transition-all"
            >
              {addMutation.isPending ? 'Adding...' : 'Add Link'}
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {links.length === 0 && !showAdd && (
        <div className="flex flex-col items-center justify-center py-16 px-4 bg-white dark:bg-[#1c2230] border border-dashed border-[#e7ebf3] dark:border-white/10 rounded-xl text-center">
          <div className="w-16 h-16 bg-violet-50 dark:bg-violet-900/20 rounded-full flex items-center justify-center mb-4">
            <span className="material-symbols-outlined text-3xl text-violet-400">link</span>
          </div>
          <h3 className="text-base font-semibold text-[#0d121b] dark:text-white mb-2">No links yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-6">
            Add your first link to start building your bio page. You can add websites, social profiles, embeds, and more.
          </p>
          <button
            onClick={() => setShowAdd(true)}
            className="px-5 py-2.5 text-sm font-medium text-white bg-[#2B6CEE] rounded-lg hover:bg-[#2454c7] shadow-sm transition-all"
          >
            Add First Link
          </button>
        </div>
      )}

      {/* Links list */}
      {links.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={links.map(l => l.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {links.map(link => (
                <SortableLinkItem
                  key={link.id}
                  link={link}
                  editingId={editingId}
                  setEditingId={setEditingId}
                  updateMutation={updateMutation}
                  deleteMutation={deleteMutation}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  )
}

function EditLinkForm({ link, onSave, onCancel, saving }: {
  link: BioLink
  onSave: (data: Partial<BioLink>) => void
  onCancel: () => void
  saving: boolean
}) {
  const [title, setTitle] = useState(link.title)
  const [url, setUrl] = useState(link.url)
  const [icon, setIcon] = useState(link.icon || '')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [utmSource, setUtmSource] = useState(link.utm_source || '')
  const [utmMedium, setUtmMedium] = useState(link.utm_medium || '')
  const [utmCampaign, setUtmCampaign] = useState(link.utm_campaign || '')
  const [utmEnabled, setUtmEnabled] = useState(link.utm_enabled ?? true)
  const [maskedSlug, setMaskedSlug] = useState(link.masked_slug || '')

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">Title</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Title"
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE]"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">Icon</label>
          <input
            type="text"
            value={icon}
            onChange={e => setIcon(e.target.value)}
            placeholder="Icon (emoji)"
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE]"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">URL</label>
        <input
          type="text"
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="URL"
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE]"
        />
      </div>

      {/* Advanced section toggle */}
      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-xs font-medium text-[#2B6CEE] hover:underline flex items-center gap-1"
        >
          <span className="material-symbols-outlined text-[16px]">{showAdvanced ? 'expand_less' : 'expand_more'}</span>
          {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
        </button>

        {showAdvanced && (
          <div className="mt-3 space-y-4 p-4 bg-gray-50 dark:bg-[#0d121b] rounded-xl border border-[#e7ebf3] dark:border-white/10">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={utmEnabled}
                onChange={e => setUtmEnabled(e.target.checked)}
                className="w-4 h-4 text-[#2B6CEE] rounded border-gray-300 focus:ring-[#2B6CEE]"
              />
              <label className="text-sm text-gray-700 dark:text-gray-300">Enable UTM parameters</label>
            </div>

            {utmEnabled && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Source</label>
                  <input
                    type="text"
                    value={utmSource}
                    onChange={e => setUtmSource(e.target.value)}
                    placeholder="e.g. instagram"
                    className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Medium</label>
                  <input
                    type="text"
                    value={utmMedium}
                    onChange={e => setUtmMedium(e.target.value)}
                    placeholder="e.g. bio_link"
                    className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Campaign</label>
                  <input
                    type="text"
                    value={utmCampaign}
                    onChange={e => setUtmCampaign(e.target.value)}
                    placeholder="e.g. spring_sale"
                    className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230]"
                  />
                </div>
              </div>
            )}

            <div className="pt-2 border-t border-[#e7ebf3] dark:border-white/10">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Masked Slug (Custom Short Link)</label>
              <div className="flex items-center">
                <span className="text-xs text-gray-400 mr-2">/</span>
                <input
                  type="text"
                  value={maskedSlug}
                  onChange={e => setMaskedSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 50))}
                  placeholder="my-link"
                  maxLength={50}
                  className="flex-1 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230]"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-2 border-t border-[#e7ebf3] dark:border-white/5">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={() => onSave({
            title,
            url,
            icon: icon || null,
            utm_source: utmSource || null,
            utm_medium: utmMedium || null,
            utm_campaign: utmCampaign || null,
            utm_enabled: utmEnabled,
            masked_slug: maskedSlug || null,
          })}
          disabled={saving}
          className="px-6 py-2 text-sm font-medium text-white bg-[#2B6CEE] rounded-lg hover:bg-[#2454c7] disabled:opacity-50 shadow-sm shadow-blue-500/20"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  )
}
